import React, { FC } from "react";
import { SearchWidgetTheme, WidgetBackgroundTheme, WidgetBorderTheme, WidgetBubbleTheme, WidgetCardTheme, WidgetCarouselTheme, WidgetLinkBlockTheme, WidgetLinkTheme, WidgetListTheme, WidgetMarginTheme, WidgetMessagesTheme, WidgetMessageTheme, WidgetPaddingTheme, WidgetTextBlockTheme, WidgetTextTheme } from "../../config/theme";

export interface WidgetStylesheetProps {
    readonly theme?: SearchWidgetTheme;
}

export function buildStyleContent(theme?: SearchWidgetTheme): string {
    return `
:root {
${buildVariables(
        withPrefix("\t--xapp-search-", union(
            single("accent-color", theme?.accentColor),
            withPrefix("widget-", union(
                getBorderStyle(theme?.border)
            )),
            withPrefix("messages-", getMessagesStyle(theme?.messages)),
            withPrefix("card-", getCardStyle(theme?.card)),
        ))
    )}
}

.xapp-search-carousel {
    ${buildVariables(
        withPrefix("\t--xapp-search-", union(
            getCarouselStyle(theme?.carousel),
        ))
    )}
}

.xapp-search-cards {
    ${buildVariables(
        withPrefix("\t--xapp-search-", union(
            getListStyle(theme?.list),
        ))
    )}
}

`;
}

export const WidgetStylesheet: FC<WidgetStylesheetProps> = props => {

    const { theme } = props;

    const stylesContent = buildStyleContent(theme);
    return (
        <style>{stylesContent}</style>
    );
}

declare type StyleGenerator = Generator<[string, string | undefined], void, never>;

function* getBorderStyle(style?: WidgetBorderTheme): StyleGenerator {
    if (style) {
        yield ['border-style', style.style];
        yield ['border-color', style.color];
        yield ['border-width', style.width];
        yield ['border-radius', style.radius];
    }
}

function buildVariables(gen: StyleGenerator) {
    let result = gen.next();
    let res = "";
    while (result.done === false) {
        const [key, value] = result.value;
        if (value !== undefined && value !== null && value !== "") {
            res += `${key}: ${value};\n\r`;
        }
        result = gen.next();
    }
    return res;
}

function* withPrefix(prefix: string, gen: StyleGenerator): StyleGenerator {
    let result = gen.next();
    while (!result.done) {
        yield [`${prefix}${result.value[0]}`, result.value[1]];
        result = gen.next();
    }
}

function* union(...children: StyleGenerator[]): StyleGenerator {
    for (const child of children) {
        let result = child.next();
        while (result.done === false) {
            yield result.value;
            result = child.next();
        }
    }
}

function* single(key: string, value: string | undefined): StyleGenerator {
    yield [key, value];
}

function* empty(): StyleGenerator {

}

function* getTextStyle(style?: WidgetTextTheme): StyleGenerator {
    if (style) {
        yield ['line-height', style.lineHeight?.toString()];
        yield ['text-color', style.color];
        yield ['font-size', style.fontSize];
        yield ['font-family', style.fontFamily];
        yield ['font-weight', style.fontWeight];
        yield ['font-style', style.fontStyle];
    }
}

function* getPaddingStyle(style?: WidgetPaddingTheme): StyleGenerator {
    if (style) {
        yield [`padding-top`, style.top];
        yield [`padding-right`, style.right];
        yield [`padding-bottom`, style.bottom];
        yield [`padding-left`, style.left];
    }
}

function* getMarginStyle(style?: WidgetMarginTheme): StyleGenerator {
    if (style) {
        yield [`margin-top`, style.top];
        yield [`margin-right`, style.right];
        yield [`margin-bottom`, style.bottom];
        yield [`margin-left`, style.left];
    }
}

function* getBackgroundStyle(style?: WidgetBackgroundTheme): StyleGenerator {
    yield ["background", style];
}

function getBubbleStyle(style?: WidgetBubbleTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(
        getBackgroundStyle(style.background),
    );
}

function getMessagesStyle(style?: WidgetMessagesTheme): StyleGenerator {
    if (!style) {
        return empty();
    }
    return union(
        // withPrefix("mine-", getMessageStyle(style.mine)),
        withPrefix("others-", getMessageStyle(style.others)),
    );
}

function getMessageStyle(style?: WidgetMessageTheme): StyleGenerator {
    return union(
        withPrefix('bubble-',
            getBubbleStyle(style?.bubble)
        ),
        getTextStyle(style?.text)
    );
}

function getLinkStyle(style?: WidgetLinkTheme): StyleGenerator {
    return union(
        getTextStyle(style?.default),
    );
}

function getLinkBlockStyle(style?: WidgetLinkBlockTheme): StyleGenerator {
    return union(
        getLinkStyle(style?.text),
        getPaddingStyle(style?.padding),
        getMarginStyle(style?.margin),
    );
}


function getTextBlockStyle(style?: WidgetTextBlockTheme): StyleGenerator {
    return union(
        getTextStyle(style?.text),
        getMarginStyle(style?.margin),
    );
}

function getCardStyle(style?: WidgetCardTheme): StyleGenerator {
    return union(
        getBackgroundStyle(style?.background),
        getBorderStyle(style?.border),
        getPaddingStyle(style?.padding),
        getMarginStyle(style?.margin),
        withPrefix("title-", getTextBlockStyle(style?.title)),
        withPrefix("description-", getTextBlockStyle(style?.description)),
        withPrefix("link-", getLinkBlockStyle(style?.link)),
    );
}

function getCarouselStyle(style?: WidgetCarouselTheme): StyleGenerator {
    return union(
        withPrefix("card-",
            getCardStyle(style?.card),
        )
    );
}

function getListStyle(style?: WidgetListTheme): StyleGenerator {
    return union(
        withPrefix("card-",
            getCardStyle(style?.card),
        )
    );
}